<script>
	import { doc, getFirestore, setDoc } from "firebase/firestore";
	import { faTrash } from "@fortawesome/free-solid-svg-icons";
	import { slugify } from "cando-util";

	import { store } from "$lib/utilities/store";
	import { recordUpdate } from "./recordUpdate.js";

	import SvelteFa from "$lib/components/SvelteFa.svelte";
	import DashboardTableField from "./DashboardTableField.svelte";

	export let col;
	export let docId;
	export let tableFields;
	export let editMode = false;
	export let group = {};

	let currentFieldOrder = Array.isArray(group.fieldOrder)
		? [...group.fieldOrder].map((f) => ({
				...f,
				fld: tableFields.find((fld) => fld.heading === f.fieldName),
			}))
		: [];
	let dragIndex = null;

	$: {
		const sourceOrder = Array.isArray(group.fieldOrder)
			? group.fieldOrder
			: [];
		if (
			sourceOrder.length !== currentFieldOrder.length ||
			sourceOrder.some(
				(field, index) => field !== currentFieldOrder[index],
			)
		) {
			currentFieldOrder = [...sourceOrder].map((f) => ({
				...f,
				fld: tableFields.find((fld) => fld.heading === f.fieldName),
			}));
		}
	}

	const persistFieldOrder = async (updatedFieldOrder) => {
		const existingGroups =
			$store.userProfile?.collectionCriteria?.[col]?.formLayout || [];
		const formLayout = existingGroups.map((existingGroup) =>
			existingGroup.id === group.id
				? { ...existingGroup, fieldOrder: updatedFieldOrder }
				: existingGroup,
		);

		const mergedCriteria = {
			...($store.userProfile?.collectionCriteria?.[col] || {}),
			formLayout,
		};

		const profileDocRef = doc(
			getFirestore(),
			"profiles",
			$store.userProfile.uid,
		);

		const updates = { collectionCriteria: { [col]: mergedCriteria } };

		await setDoc(profileDocRef, updates, { merge: true });
	};

	const reorderField = async (targetIndex) => {
		if (dragIndex === null || dragIndex === targetIndex) return;

		const updatedFieldOrder = [...currentFieldOrder];
		const [movedField] = updatedFieldOrder.splice(dragIndex, 1);
		updatedFieldOrder.splice(targetIndex, 0, movedField);

		dragIndex = null;
		currentFieldOrder = [...updatedFieldOrder];

		for (const f of updatedFieldOrder) delete f.fld;

		await persistFieldOrder(updatedFieldOrder);
	};

	const handleDragStart = (event, index) => {
		if (!editMode) return;

		dragIndex = index;
		event.dataTransfer?.setData("text/plain", currentFieldOrder[index]);
	};

	const handleDrop = async (event, index) => {
		if (!editMode) return;

		event.preventDefault();
		event.stopPropagation();
		await reorderField(index);
	};

	const handleDragOver = (event) => {
		if (!editMode || dragIndex === null) return;

		event.preventDefault();
	};

	const handleDragEnd = () => {
		dragIndex = null;
	};

	const handleContainerDragOver = (event) => {
		if (!editMode || dragIndex === null) return;
		event.preventDefault();
	};

	const handleContainerDrop = async (event) => {
		if (!editMode) return;
		event.preventDefault();
		await reorderField(currentFieldOrder.length);
	};

	const removeFieldFromGroup = async (field) => {
		const formLayout = (
			$store.userProfile?.collectionCriteria?.[col]?.formLayout || []
		).map((g) =>
			g.id === group.id
				? {
						...g,
						fieldOrder: (g.fieldOrder || []).filter(
							(f) => f.fieldName !== field.fieldName,
						),
					}
				: g,
		);

		const updates = {
			collectionCriteria: {
				[col]: {
					...$store.userProfile?.collectionCriteria?.[col],
					formLayout,
				},
			},
		};

		const profileDocRef = doc(
			getFirestore(),
			"profiles",
			$store.userProfile.uid,
		);

		await setDoc(profileDocRef, updates, { merge: true });
	};

	const updateFieldClass = async (field, newClass) => {
		const formLayout = (
			$store.userProfile?.collectionCriteria?.[col]?.formLayout || []
		).map((g) =>
			g.id === group.id
				? {
						...g,
						fieldOrder: (g.fieldOrder || []).map((f) => ({
							...f,
							...(f.fieldName === field.fieldName
								? { fieldClass: newClass }
								: {}),
						})),
					}
				: g,
		);

		const updates = {
			collectionCriteria: {
				[col]: {
					...$store.userProfile?.collectionCriteria?.[col],
					formLayout,
				},
			},
		};

		const profileDocRef = doc(
			getFirestore(),
			"profiles",
			$store.userProfile.uid,
		);

		await setDoc(profileDocRef, updates, { merge: true });
	};

	const createField = (e) => {
		const elem = e.target;
		const key = elem.getAttribute("key");
		const updates = {
			[key]: {
				translations: {},
				value: "",
				type: "TextInput",
				permission: "Admin",
			},
		};
		void recordUpdate(col, docId, updates);
		console.log({ key, col, docId });
		console.log("create field");
	};

	// alwaysRender keeps a field mounted before its key exists, for renderers that
	// source data from outside the record (a live WebSocket feed, a linked doc).
	// A stale fieldOrder entry resolves to an undefined fld, so guard before use.
	$: visibleFields = currentFieldOrder.filter(
		(f) =>
			editMode ||
			!f?.fld?.key ||
			f.fld.alwaysRender === true ||
			$store?.[col]?.docs?.[docId]?.[f.fld.key] !== undefined,
	);
</script>

<div
	class={`grid gap-4 ${group?.fieldContainerClass}`}
	role={editMode ? "list" : undefined}
	on:dragover={handleContainerDragOver}
	on:drop={handleContainerDrop}
>
	{#each visibleFields as field, index}
		<div
			class={`flex flex-col space-2 sm:space-4 bg-base-100 rounded-xl ${editMode ? "hover:cursor-pointer" : ""} ${field.fieldClass}`}
			draggable={editMode}
			role={editMode ? "listitem" : undefined}
			on:dragstart={(event) => handleDragStart(event, index)}
			on:dragover={handleDragOver}
			on:drop={(event) => handleDrop(event, index)}
			on:dragend={handleDragEnd}
		>
			<div class="flex items-start justify-between gap-2">
				<label
					for={`${slugify(field.fieldName)}-input`}
					class="text-xs bg-primary/20 w-full p-2 rounded-t-xl"
				>
					{field.fieldName}
				</label>
				{#if editMode}
					{#if field.fld?.key}
						{#if $store?.[col]?.docs?.[docId]?.[field.fld.key] === undefined}
							<button
								class="btn btn-sm btn-primary"
								on:click={createField}
								key={field.fld.key}>Create</button
							>
						{/if}
					{/if}
					<div class="flex items-center gap-2">
						<input
							type="text"
							class="input input-bordered input-primary input-xs"
							placeholder="Field classes"
							value={field.fieldClass}
							on:change={(event) =>
								updateFieldClass(
									field,
									event.currentTarget.value,
								)}
						/>
						<button
							class="btn btn-xs btn-ghost text-error gap-1"
							aria-label={`Remove ${field.fieldName} from group`}
							title="Remove field"
							type="button"
							on:click={() => removeFieldFromGroup(field)}
						>
							<SvelteFa icon={faTrash} />
						</button>
					</div>
				{/if}
			</div>
			<div class="flex-1 w-full p-2">
				<DashboardTableField
					fld={field.fld}
					{col}
					{docId}
					asBlock={field.fld?.type === "CallTranscription"}
					docs={$store?.[col]?.docs || {}}
					action={() => {}}
					{editMode}
				/>
			</div>
		</div>
	{/each}
</div>
